import { API_BASE, DEMO_CLIENT_ID } from './config';
import type { ClientConfig, ConversationTurn, Lead, Session } from './types';

async function req<T>(session: Session, path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${session.idToken}`,
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`${res.status} ${text.slice(0, 200)}`);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

function cid(session: Session) {
  return session.groups.includes('admin') ? DEMO_CLIENT_ID : session.clientId;
}

function scope(session: Session) {
  return `clientId=${encodeURIComponent(cid(session))}`;
}

/** GET /api/config. No fake fallback: a failed load must never be edited and saved over real settings. */
export async function getConfig(session: Session): Promise<ClientConfig> {
  const data = await req<{ config?: ClientConfig } & Partial<ClientConfig>>(session, `/api/config?${scope(session)}`);
  const config = (data.config ?? data) as ClientConfig;
  if (!config.clientId) throw new Error('Config not found');
  return config;
}

export async function saveConfig(session: Session, patch: Partial<ClientConfig>): Promise<void> {
  await req(session, `/api/config?${scope(session)}`, { method: 'PATCH', body: JSON.stringify(patch) });
}

const DEMO_LEAD: Lead = {
  clientId: DEMO_CLIENT_ID,
  leadId: '+15551230001',
  phone: '+15551230001',
  name: 'Demo Homeowner',
  source: 'missed-call',
  status: 'in-conversation',
  contactCount: 1,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
};

const DEMO_TURNS: ConversationTurn[] = [
  { clientId: DEMO_CLIENT_ID, leadId: '+15551230001', timestamp: new Date().toISOString(), seq: 0, role: 'assistant', text: 'LeadsIRL: sorry we missed your call. What service are you looking for? Reply STOP to opt out.', channel: 'sms' },
  { clientId: DEMO_CLIENT_ID, leadId: '+15551230001', timestamp: new Date().toISOString(), seq: 1, role: 'lead', text: 'Need a roof estimate in Walled Lake', channel: 'sms' },
  { clientId: DEMO_CLIENT_ID, leadId: '+15551230001', timestamp: new Date().toISOString(), seq: 2, role: 'assistant', text: 'Got it. What zip is the house in?', channel: 'sms' },
];

export async function listLeads(session: Session): Promise<Lead[]> {
  const id = cid(session);
  const data = await req<{ leads?: Lead[] } | Lead[]>(
    session,
    `/api/leads?clientId=${encodeURIComponent(id)}`,
  );
  return Array.isArray(data) ? data : data.leads ?? [];
}

export async function getConversation(session: Session, leadId: string): Promise<ConversationTurn[]> {
  const id = cid(session);
  const data = await req<{ turns?: ConversationTurn[] } | ConversationTurn[]>(
    session,
    `/api/leads/${encodeURIComponent(leadId)}/conversation?clientId=${encodeURIComponent(id)}`,
  );
  return Array.isArray(data) ? data : data.turns ?? [];
}

export async function patchLead(session: Session, leadId: string, attrs: Partial<Lead>): Promise<void> {
  await req(session, `/api/leads/${encodeURIComponent(leadId)}?${scope(session)}`, { method: 'PUT', body: JSON.stringify(attrs) });
}

// ------------------------------------------------------------ admin Agent screen
export interface AgentPreview {
  defaults: Record<string, string>;
  sections: Record<string, string>;
  overridden: string[];
  complianceEnabled: boolean;
  handoffEnabled: boolean;
  handoffPausesAi?: boolean;
  maxReplyChars: number;
  temperature: number;
  modelId: string;
  defaultModelId: string;
  newLeadPrompt: string;
  returningLeadPrompt: string;
  openingEvent: string;
  tools: string[];
}
export interface ModelOption { id: string; name: string; provider: string; kind: 'direct' | 'us' | 'global' | 'other' }
export interface ModelTest { ok: boolean; modelId: string; latencyMs: number; reply?: string; error?: string }

/** Config plus (admins only) every instruction the AI gets and a live preview. */
export async function getAgent(session: Session): Promise<{ config: ClientConfig; agent?: AgentPreview }> {
  const data = await req<{ config: ClientConfig; agent?: AgentPreview }>(session, `/api/config?${scope(session)}`);
  if (!data.config?.clientId) throw new Error('Config not found');
  return data;
}

/** PATCH any settings the portal accepts. The server validates and enforces admin-only fields. */
export async function saveSettings(session: Session, patch: Record<string, unknown>): Promise<void> {
  await req(session, `/api/config?${scope(session)}`, { method: 'PATCH', body: JSON.stringify(patch) });
}

/** Live from AWS every call. */
export async function listModels(session: Session) {
  return req<{ models: ModelOption[]; defaultModelId: string; fetchedAt: string }>(session, `/api/agent/models?${scope(session)}`);
}

export async function testModel(session: Session, modelId: string) {
  return req<ModelTest>(session, `/api/agent/test-model?${scope(session)}`, { method: 'POST', body: JSON.stringify({ modelId }) });
}

// ------------------------------------------------------------ owner replies
/** Text a lead from the shop's number. The server pauses the AI on that lead. */
export async function sendMessage(session: Session, leadId: string, text: string) {
  return req<{ ok: boolean; turn: ConversationTurn; aiPaused: boolean }>(
    session, `/api/leads/${encodeURIComponent(leadId)}/messages?${scope(session)}`,
    { method: 'POST', body: JSON.stringify({ text }) },
  );
}

export async function setAiPaused(session: Session, leadId: string, paused: boolean) {
  return req<{ ok: boolean; aiPaused: boolean }>(
    session, `/api/leads/${encodeURIComponent(leadId)}/ai?${scope(session)}`,
    { method: 'POST', body: JSON.stringify({ paused }) },
  );
}

// ------------------------------------------------------------ lead details
export interface LeadField { key: string; label: string; question: string; ask: boolean; required: boolean }

/** The shop's Info to collect list (built-in defaults if never edited) and the auto-Qualified switch. */
export async function getLeadSettings(session: Session) {
  const d = await req<{ config: ClientConfig & { autoQualify?: boolean }; fields?: LeadField[] }>(session, `/api/config?${scope(session)}`);
  return { fields: d.fields ?? [], autoQualify: d.config?.autoQualify !== false };
}

export async function getLeadFields(session: Session): Promise<LeadField[]> {
  return (await getLeadSettings(session)).fields;
}

export async function getLeadById(session: Session, leadId: string): Promise<Lead> {
  const d = await req<{ lead: Lead }>(session, `/api/leads/${encodeURIComponent(leadId)}?${scope(session)}`);
  return d.lead;
}

/** Owner edits. Empty value clears the field so the AI can fill it again. */
export async function saveLeadDetails(session: Session, leadId: string, details: Record<string, string>) {
  return req<{ ok: boolean; lead: Lead }>(
    session, `/api/leads/${encodeURIComponent(leadId)}/details?${scope(session)}`,
    { method: 'PUT', body: JSON.stringify({ details }) },
  );
}

// ------------------------------------------------------------ calendar
export interface CalendarSettings {
  bookingEnabled: boolean;
  calendarId: string;
  timezone: string;
  businessHours: { start: string; end: string; days: number[] };
  appointmentDurationMinutes: number;
  bookingBufferMinutes: number;
  bookingLeadHours: number;
  bookingDaysAhead: number;
}
export interface CalendarInfo {
  connected: boolean;
  provider: string | null;
  connectedAt: string | null;
  calendarError: string;
  settings: CalendarSettings;
  openTimes: { startIso: string; label: string }[];
  upcoming: { leadId: string; name?: string; phone?: string; appointmentAt?: string; service?: string; address?: string; summary?: string }[];
}

export async function getCalendar(session: Session) {
  return req<CalendarInfo>(session, `/api/calendar?${scope(session)}`);
}

/** Returns the Google sign-in URL. The browser goes there, then comes back to the CRM. */
export async function startCalendarConnect(session: Session, provider: 'google' | 'outlook' = 'google') {
  const r = await req<{ authorizeUrl: string }>(session, `/api/oauth/start?${scope(session)}`, {
    method: 'POST', body: JSON.stringify({ provider }),
  });
  return r.authorizeUrl;
}

export async function disconnectCalendar(session: Session) {
  await req(session, `/api/calendar/disconnect?${scope(session)}`, { method: 'POST', body: '{}' });
}
